/**
 * Lista del message log: la pantalla de entrada del diagnostico.
 *
 * Los filtros viven en la query string (ver `lib/filters.ts`), asi que un enlace
 * a "todo lo fallido de SAP en F10 entre las 9 y las 10" se pega en un canal de
 * soporte y el que lo abre ve exactamente lo mismo.
 *
 * El filtrado real lo hace la API (el log es grande y crece rapido). La "busqueda
 * rapida" es un refino local sobre la pagina ya cargada, para no ir a la red
 * mientras se tipea.
 *
 * La lista se actualiza sola cada 10 s (se puede apagar desde el encabezado).
 * El refresco es silencioso: no vuelve a "Cargando…" ni rehace la tabla, asi que
 * la busqueda rapida, los filtros a medio escribir y el scroll quedan donde
 * estaban.
 *
 * En un telefono los filtros se pliegan tras un boton y cada fila se lee como
 * una tarjeta (ver `.filters-panel` y `.table--stack` en global.css).
 */
import { useEffect, useId, useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type { MessageFilters, PageState } from '../api/types';
import { useMonitorApi } from '../app/api-context';
import { AutoRefreshToggle } from '../components/AutoRefreshToggle';
import { ErrorBanner } from '../components/ErrorBanner';
import { EventName } from '../components/EventName';
import { EmptyState, LoadingBlock } from '../components/Feedback';
import { IconChevronRight, IconFilter } from '../components/icons';
import { Pagination } from '../components/Pagination';
import { SelectFilter } from '../components/SelectFilter';
import { DirectionBadge, StatusBadge } from '../components/StatusBadge';
import { useAutoRefreshPreference } from '../hooks/use-auto-refresh-preference';
import { usePolledResource } from '../hooks/use-polled-resource';
import { REFRESH_INTERVAL_MS } from '../lib/auto-refresh';
import {
  DIRECTION_OPTIONS,
  FLOW_OPTIONS,
  STATUS_OPTIONS,
  SYSTEM_OPTIONS,
  flowLabel,
  systemLabel,
} from '../lib/catalogs';
import {
  EMPTY_MESSAGE_FILTERS,
  activeMessageFilterCount,
  matchesQuickSearch,
  messageFiltersFromSearchParams,
  messageFiltersToSearchParams,
  pageStateFromSearchParams,
} from '../lib/filters';
import { formatDateTime, shortId } from '../lib/format';
import '../styles/flow-chip.css';

/** Nombre del boton que pliega los filtros: el numero del globo, dicho con palabras. */
function filtersToggleLabel(active: number): string {
  return active === 0 ? 'Filtros' : `Filtros: ${active} ${active === 1 ? 'activo' : 'activos'}`;
}

export function MessagesPage(): ReactElement {
  const api = useMonitorApi();
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = useMemo(() => messageFiltersFromSearchParams(searchParams), [searchParams]);
  const page = useMemo(() => pageStateFromSearchParams(searchParams), [searchParams]);
  const queryKey = searchParams.toString();

  // Borrador de los campos de texto y fecha: se aplican al enviar el formulario,
  // no en cada tecla, para no disparar una consulta por caracter.
  const [draft, setDraft] = useState<MessageFilters>(filters);
  useEffect(() => {
    setDraft(filters);
  }, [filters]);

  const [quickSearch, setQuickSearch] = useState('');
  // Solo pesa en un telefono: en escritorio el formulario se ve siempre.
  const [filtersOpen, setFiltersOpen] = useState(false);
  const filtersId = useId();

  const [autoRefresh, setAutoRefresh] = useAutoRefreshPreference('messages');
  const resource = usePolledResource(() => api.listMessages(filters, page), [api, queryKey], {
    intervalMs: REFRESH_INTERVAL_MS.messages,
    enabled: autoRefresh,
  });

  const apply = (next: MessageFilters, nextPage: PageState = { ...page, page: 1 }): void => {
    setSearchParams(messageFiltersToSearchParams(next, nextPage));
  };

  const rows = useMemo(() => {
    const items = resource.data?.items ?? [];
    return items.filter((item) => matchesQuickSearch(item, quickSearch));
  }, [resource.data, quickSearch]);

  const activeFilters = activeMessageFilterCount(filters);

  return (
    <div className="page">
      <header className="page__header">
        <div>
          <h1>Mensajes</h1>
          <p className="page__subtitle">
            Todo mensaje entrante y saliente queda registrado con su payload crudo, su payload
            normalizado y su historial de intentos.
          </p>
        </div>
        <div className="row">
          <AutoRefreshToggle
            enabled={autoRefresh}
            onChange={setAutoRefresh}
            intervalMs={REFRESH_INTERVAL_MS.messages}
            updatedAt={resource.updatedAt}
            failing={resource.error !== null}
          />
          <button type="button" className="button" onClick={resource.reload}>
            Actualizar
          </button>
        </div>
      </header>

      <section
        className={filtersOpen ? 'panel filters-panel filters-panel--open' : 'panel filters-panel'}
      >
        <button
          type="button"
          className="filters-panel__toggle"
          aria-expanded={filtersOpen}
          aria-controls={filtersId}
          aria-label={filtersToggleLabel(activeFilters)}
          onClick={() => setFiltersOpen((open) => !open)}
        >
          <IconFilter size={16} />
          Filtros
          {activeFilters === 0 ? null : (
            <span className="filters-panel__count">{activeFilters}</span>
          )}
          <IconChevronRight size={16} className="filters-panel__chevron" />
        </button>
        <div id={filtersId} className="panel__body filters-panel__body">
          <form
            className="filters"
            onSubmit={(event) => {
              event.preventDefault();
              apply(draft);
            }}
          >
            <SelectFilter
              label="Sistema"
              value={draft.system}
              options={SYSTEM_OPTIONS}
              onChange={(value) => apply({ ...draft, system: value })}
            />
            <SelectFilter
              label="Estado"
              value={draft.status}
              options={STATUS_OPTIONS}
              onChange={(value) => apply({ ...draft, status: value })}
            />
            <SelectFilter
              label="Flujo"
              value={draft.flow}
              options={FLOW_OPTIONS}
              onChange={(value) => apply({ ...draft, flow: value })}
            />
            <SelectFilter
              label="Dirección"
              value={draft.direction}
              options={DIRECTION_OPTIONS}
              onChange={(value) => apply({ ...draft, direction: value })}
            />

            <label className="field field--wide">
              <span className="field__label">Correlation id</span>
              <input
                className="input input--mono"
                type="search"
                value={draft.correlationId}
                placeholder="id del hilo, o el número del pedido (PV-…)"
                onChange={(event) => setDraft({ ...draft, correlationId: event.target.value })}
              />
            </label>

            <label className="field">
              <span className="field__label">Evento</span>
              <input
                className="input"
                type="search"
                value={draft.eventType}
                placeholder="order.created"
                onChange={(event) => setDraft({ ...draft, eventType: event.target.value })}
              />
            </label>

            <label className="field field--datetime">
              <span className="field__label">Desde</span>
              <input
                className="input"
                type="datetime-local"
                value={draft.from}
                onChange={(event) => setDraft({ ...draft, from: event.target.value })}
              />
            </label>

            <label className="field field--datetime">
              <span className="field__label">Hasta</span>
              <input
                className="input"
                type="datetime-local"
                value={draft.to}
                onChange={(event) => setDraft({ ...draft, to: event.target.value })}
              />
            </label>

            <div className="row">
              <button type="submit" className="button button--primary">
                Aplicar
              </button>
              <button
                type="button"
                className="button"
                disabled={activeFilters === 0}
                onClick={() => apply(EMPTY_MESSAGE_FILTERS)}
              >
                Limpiar{activeFilters === 0 ? '' : ` (${activeFilters})`}
              </button>
            </div>
          </form>
        </div>
      </section>

      <ErrorBanner error={resource.error} onRetry={resource.reload} />

      <section className="panel">
        <header className="panel__header">
          <h2>Resultados</h2>
          <label className="row panel__search">
            <span className="visually-hidden">Búsqueda rápida en la página</span>
            <input
              className="input"
              type="search"
              placeholder="Búsqueda rápida en la página"
              value={quickSearch}
              onChange={(event) => setQuickSearch(event.target.value)}
            />
          </label>
        </header>

        {resource.loading && resource.data === null ? (
          <LoadingBlock />
        ) : rows.length === 0 ? (
          <EmptyState
            title="Ningún mensaje calza con los filtros."
            hint="Prueba ampliando el rango de fechas o quitando el filtro de estado."
          />
        ) : (
          <div className="table__wrap">
            <table className="table table--stack">
              <thead>
                <tr>
                  <th scope="col">Recibido</th>
                  <th scope="col">Sistema</th>
                  <th scope="col">Dir.</th>
                  <th scope="col">Flujo</th>
                  <th scope="col">Evento</th>
                  <th scope="col">Estado</th>
                  <th scope="col">Int.</th>
                  <th scope="col">HTTP</th>
                  <th scope="col">Correlation id</th>
                  <th scope="col">Último error</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td className="nowrap" data-label="Recibido">
                      <Link className="table__open" to={`/mensajes/${row.id}`}>
                        {formatDateTime(row.receivedAt)}
                      </Link>
                    </td>
                    <td className="nowrap" data-label="Sistema">
                      {systemLabel(row.system)}
                    </td>
                    <td className="table__pill">
                      <DirectionBadge direction={row.direction} />
                    </td>
                    <td className="nowrap" data-label="Flujo">
                      {/* El flujo lleva a la corrida completa del mensaje, no a su guía. */}
                      {row.flow ? (
                        <Link
                          className="flow-chip"
                          to={`/flujo/${encodeURIComponent(row.correlationId)}`}
                          title={flowLabel(row.flow)}
                          aria-label={`${row.flow}: ver el flujo completo de la corrida`}
                        >
                          {row.flow}
                        </Link>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="mono table__lead">
                      <EventName value={row.eventType} />
                    </td>
                    <td className="table__pill">
                      <StatusBadge status={row.status} />
                    </td>
                    <td data-label="Intentos">{row.attempts}</td>
                    <td data-label="HTTP">
                      {/* Un 400 procesado se ve como contradiccion sin la pista de la nota
                          (el -4006 de SAP): la nota completa va en el title y en el detalle. */}
                      {row.note === null || row.note === undefined ? (
                        (row.httpStatus ?? '—')
                      ) : (
                        <span className="muted" title={row.note}>
                          {`${row.httpStatus === null ? '—' : String(row.httpStatus)} · nota`}
                        </span>
                      )}
                    </td>
                    <td data-label="Correlation id">
                      <Link
                        className="mono"
                        to={`/traza/${encodeURIComponent(row.correlationId)}`}
                        title={row.correlationId}
                      >
                        {shortId(row.correlationId)}
                      </Link>
                    </td>
                    {/* Rojo solo si hay error: un "—" rojo en cada fila sana se lee como alarma. */}
                    {row.lastError === null ? (
                      <td className="muted table__placeholder" data-label="Último error">
                        —
                      </td>
                    ) : (
                      <td className="table__error table__cell--wide" data-label="Último error">
                        <span className="truncate" title={row.lastError}>
                          {row.lastError}
                        </span>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <Pagination
          page={page.page}
          pageSize={page.pageSize}
          total={resource.data?.total ?? 0}
          onPageChange={(nextPage) => apply(filters, { ...page, page: nextPage })}
          onPageSizeChange={(pageSize) => apply(filters, { page: 1, pageSize })}
        />
      </section>
    </div>
  );
}
